import { Component } from "../Component.js";
import { Entity } from "../Entity.js";
import { getLogger } from "../Logger.js";
import { LocalSystemMessage, ReceivedLocalSystemMessage, ReceivedMessage } from "../types.js";
import { Process } from "./Process.js";
import { type DebugLogger, NOOP_DEBUG_LOGGER } from "../debug/types.js";
import type { AssetChange } from "../assets/Asset.js";

/**
 * Basic system message shape.
 */
export type Message = { 
  type: string | number,
  to: string | number,
  data: any,
}

import { Logger } from 'pino';

/**
 * System dependency metadata.
 */
export type SystemDependency = ({
  name: string | number,
  version?: string,
} | string | number);

/**
 * System constructor params.
 */
export type SystemParams = {
  name: string | number,
  version?: string,
  dependencies?: SystemDependency[],
  apiDependencies?: string[],
  options?: any,
}
type CallerApiFactory = (caller: System) => any;

/**
 * Open interface for shared API registration via declaration merging.
 *
 * Shared systems augment this interface to declare their API methods.
 * Both ClientApiRegistry and ServerApiRegistry automatically include
 * SharedApiRegistry members.
 *
 * @example
 * ```typescript
 * declare module '@gotti/core' {
 *   interface SharedApiRegistry {
 *     walkPlayerTo: (x: number, y: number) => Promise<boolean>;
 *   }
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SharedApiRegistry {}

/**
 * Base class for ECS systems.
 *
 * The `TProvides` generic constrains which keys this system can register
 * via `addApi()` and also appears on `$api` so the system can see its own
 * registered APIs in autocomplete. `ClientSystem()` / `ServerSystem()`
 * narrow `$api` further to include `ClientApiRegistry` / `ServerApiRegistry`.
 */
export class System<TProvides = {}, TComponent = {} | Component> {
  public _logger: Logger
  public process: Process
  readonly name : string | number
  public $api : TProvides & SharedApiRegistry;
  public $gameData : any;
  // public $gameState : any;

  /**
   * Unified per-process asset accessor (namespaced client/server asset
   * access — read/write on the server, read-only on the client). Injected
   * once by the owning process's `onAddedSystem` (`attachServerAssets` /
   * `attachClientAssets`) — see `packages/server/src/assets/inject.ts` /
   * `packages/client/src/assets/inject.ts`.
   *
   * Loosely typed here (both sides share this base class); `IServerSystem`
   * and the client system interface narrow it to the concrete
   * `ServerAssetsFacade` / `ClientAssetsFacade` shape AND mark it REQUIRED —
   * every server/client system gets it injected before its lifecycle runs, so
   * `this.$assets` needs no `!`. Kept optional + `unknown` only here on the
   * shared base, for plain systems constructed off-process (e.g. a bare unit
   * test) or the sandbox global runtime.
   */
  public $assets?: unknown;

  /**
   * Debug logger for this system.
   * Use this instead of console.log for debug output that appears in the debug UI.
   * Can be called directly as a function or with .log/.info/.warn/.error methods.
   * @example
   * this.$debugLogger('Player moved', position);
   * this.$debugLogger.info('Game started');
   * this.$debugLogger.group('position').log(x, y); // Grouped logs replace each other
   */
  public $debugLogger: DebugLogger = NOOP_DEBUG_LOGGER;

  // ── Auto-tracked entity/component collections ──────────────────────
  // Automatically maintained when entities add/remove this system's component.
  // Use $entities / $components for O(1) lookup by entity ID.
  // Use $entitiesArray / $componentsArray for fast cached iteration (lazy, dirty-flagged).

  /** Map of entityId → Entity for all entities with this system's component.
   *  Keys are STRING entity ids — a numeric id is coerced to string at the
   *  tracking boundary, so always look up with a string (e.g. the `clientId`
   *  from `onClientMessage`, or `String(entity.id)`). */
  readonly $entities: Map<string, Entity> = new Map();
  /** Map of entityId → TComponent for all entities with this system's
   *  component. String-keyed, same as `$entities` (numeric ids are coerced). */
  readonly $components: Map<string, TComponent> = new Map();

  /** @internal Cached array, rebuilt lazily when dirty */
  private _entitiesArray: Entity[] | null = null;
  /** @internal Cached array, rebuilt lazily when dirty */
  private _componentsArray: TComponent[] | null = null;

  /** Cached array of tracked entities. Rebuilt only after add/remove mutations. */
  get $entitiesArray(): Entity[] {
    if (this._entitiesArray === null) {
      this._entitiesArray = [...this.$entities.values()];
    }
    return this._entitiesArray;
  }

  /** Cached array of tracked components. Rebuilt only after add/remove mutations. */
  get $componentsArray(): TComponent[] {
    if (this._componentsArray === null) {
      this._componentsArray = [...this.$components.values()];
    }
    return this._componentsArray;
  }

  /** @internal Invalidate cached arrays after entity/component add or remove. */
  private _invalidateEntityCache() {
    this._entitiesArray = null;
    this._componentsArray = null;
  }

  /** @internal Called by the framework before the user's onEntityAddedComponent hook. */
  public __trackEntity(entity: Entity, component: TComponent) {
    // Normalize to a string key so the maps are honestly string-typed while a
    // numeric `entity.id` still works at runtime (coerce, don't restrict).
    const key = String(entity.id);
    if (this.$entities.has(key)) {
      throw new Error(`System '${this.name}': entity '${key}' is already tracked. This usually means two entities share the same id, which is a bug.`);
    }
    this.$entities.set(key, entity);
    this.$components.set(key, component);
    this._invalidateEntityCache();
  }

  /** @internal Called by the framework before the user's onEntityRemovedComponent hook. */
  public __untrackEntity(entity: Entity) {
    const key = String(entity.id);
    this.$entities.delete(key);
    this.$components.delete(key);
    this._invalidateEntityCache();
  }

  readonly pooledAddedApi : Record<string, any> = {};
    // pool for caller-aware apis added before process is set
  readonly pooledAddedCallerApi: Record<string, CallerApiFactory> = {};
  public entityEventHandlers: {[entityId: string]: { 
      entity: Entity,
      listeners: {
      [eventName: string]: (...args: any[]) => any
  }}} = {};

  public __version: string = '0.0.0';
  readonly __system_deps? : SystemDependency[];
  readonly __api_deps?: string[];
  readonly __options?: any;
  constructor(name: string | number | SystemParams) {
    if(typeof name === 'object') {
      this.name = name.name;
      this.__version = name.version || '0.0.0';
      if(name.dependencies?.length) {
        this.__system_deps = name.dependencies
      }
      if(name.apiDependencies?.length) {
        this.__api_deps = name.apiDependencies;
      }
      this.__options = name.options;
    } else {
      this.name = name;
    }
  }

  get $options() {
    return this.__options || {};
  }
  set $options(options: any) {
    throw new Error(`Reserved framework prop - Cannot set $options directly on systems.`);
  }
  get logger() {
    if(!this._logger) {
      this.createLogger();
    }
    return this._logger;
  }

  get $version() {
    return this.__version;
  } 

  set $version(version: string) {
    throw new Error(`Reserved framework prop - Cannot set $version directly on systems.`);
  } 

  // make this take in a TComponent constructor
  public tryComponent<T extends Component>(entity: Entity, component: new (...args: any[]) => T, cb: (component: T) => void) {
    if(!entity || !component) return undefined;
    return entity.tryComponent(component, cb);
  }

  public tryComponents<T extends Component>(entities: Entity[], component: new (...args: any[]) => T, cb: (components: (T | undefined)[]) => void) {
    const foundComponents: (T | undefined)[] = entities.map(e => {
      return e.getComponentByConstructor(component);
    });
    cb(foundComponents);
  }

  // Similar to tryComponent, but for narrowing an Entity instance to a subclass/type via constructor
  public tryEntity<T extends Entity>(entity: Entity, ctor: new (...args: any[]) => T, cb: (typedEntity: T) => void): T | undefined {
    if(!entity || !ctor) return undefined;
    if(entity instanceof (ctor as any)) {
      const typed = entity as T;
      cb(typed);
      return typed;
    }
    return undefined;
  }

  // overload: provide constructor and callback
  public trySystemComponent<T extends Component>(entity: Entity, component: new (...args: any[]) => T, cb: (component: T) => void): T | undefined;
  // overload: infer component type from system's second generic and only provide a callback
  public trySystemComponent(entity: Entity, cb: (component: TComponent) => void): TComponent | undefined;
  public trySystemComponent(entity: Entity, componentOrCb: any, cb?: any) {
    if(!entity || !componentOrCb) return undefined;

    // If a constructor was passed (it will be a function whose prototype is a Component)
    if(typeof componentOrCb === 'function' && componentOrCb.prototype instanceof Component) {
        return entity.tryComponent(componentOrCb, cb);
    }

    // Otherwise treat the second argument as the callback and use the system's registered component name
    const callback = componentOrCb as (comp: TComponent) => void;
    const comp = this.getSystemComponent(entity as any);
    if(comp) callback(comp);
    return comp;
  }

  public createLogger<T extends Record<string, string | number>>(nameOrEnumLookup?: string | T) {
      let name = this.name;
      if(nameOrEnumLookup) {
          if(typeof nameOrEnumLookup === 'string') {
              name = nameOrEnumLookup;
          } else {
            const foundValue = Object.keys(nameOrEnumLookup).find(key => nameOrEnumLookup[key as keyof T] === this.name) as keyof T | undefined;
            if(foundValue) {
                name = foundValue.toString();
            } else {
              throw new Error(`System: ${this.name} was not found in the enum provided to createLogger. `)
            }
          }
      }
      // A framework logger: getLogger asks the process's logger factory (setLoggerFactory — the CLI's, an embedder's), so
      // a game's system records go where its servers' go, else it is core's default (createLogger). Typed as pino's
      // Logger, the type `logger` has always had: core's default and the CLI's loggers are pino's; a factory's own is
      // promised the six level methods (GottiLogger), which is what systems call.
      this._logger = getLogger(`System:${name}:`, { kind: 'system' }) as Logger;
  }

  public getSystemComponent(entity: Pick<Entity, 'getComponent'>) : TComponent {
      return entity?.getComponent(this.name) as TComponent;
  }

  public getSystemComponents(entities: Pick<Entity, 'getComponent'>[]) : TComponent[] {
      return entities.map(e => this.getSystemComponent(e));
  }
  
  
  //overrided in Process.ts _addSystem
  public initializeEntity:(entity: Entity, data?: any) => Entity;
  public destroyEntity: (entity: Entity, options?: any) => void;

  public __init() {
    if(!this.process) throw new Error(`Should not be initializing a system without a process...`);
    const missingDeps = this.process.getMissingSystemDependencies(this.__system_deps);
    if(missingDeps.length) {
      throw new Error(`System: ${this.name} has dependencies: ${missingDeps.map(d => typeof d === 'object' ? ('version' in d ? `${d.name} v${d.version}` : d.name) : d).join(', ')} but they are not available in the process.`);
    }
    const missingApiDeps = this.process.getMissingApiDependencies(this.__api_deps);
    if(missingApiDeps.length) {
      throw new Error(`System: ${this.name} requires $api dependencies: ${missingApiDeps.join(', ')} but they are not available in the process. Make sure to define this with this.addApi(<depName>, <depValue>) in the constructor or onInit method of any registered gotti systems.`);
    }
    this.onInit?.();
    
    (this as any).addApi = (key: any) => {
      throw new Error(`System: ${this.name} is trying to add api property: ${key} but addApi should only be called in the constructor or in onInit. `);
    }
  }

  /**
   * Register an API value on the shared `$api` object.
   *
   * When `TProvides` is explicitly set (via `ClientSystem<MyApi>()`
   * or `ClientSystem(Base, api<MyApi>())`), only declared keys are
   * accepted — typos and wrong names produce a compile-time error.
   */
  public addApi<K extends keyof TProvides & string>(name: K, value: TProvides[K], _systemToBind?: 'original'): void;
  public addApi<K extends keyof TProvides & string>(config: { name: K, system?: (caller: System) => any, handler?: TProvides[K] }): void;
  public addApi(nameOrConfig: any, value?: any, _systemToBind?: any): void {
    if (typeof nameOrConfig === 'object' && nameOrConfig && 'name' in nameOrConfig) {
      const cfg = nameOrConfig as { name: string, system?: (caller: System) => any, handler?: any };
      if (this.process) {
        (this.process as any).addApi(cfg, this);
      } else {
        // pool: store handler values and caller factories
        if (cfg.handler !== undefined) {
          this.pooledAddedApi[cfg.name] = cfg.handler;
        }
        if (cfg.system) {
          this.pooledAddedCallerApi[cfg.name] = cfg.system as any;
        }
      }
      return;
    }
    // legacy name/value path
    if(this.process) {
      (this.process as any).addApi(nameOrConfig, value, this);
    } else {
      this.pooledAddedApi[nameOrConfig] = value;
    }
  }

    // called in Process.ts in _addSystem
    public setProcess(process: Process) {
      this.process = process;
      // assign a snapshot for this system (rebuilt on add/remove)
      Object.defineProperty(this, '$gameData', {
        value: this.process.$gameData,
        writable: false
      })
      for(const key in this.pooledAddedApi) {
        this.process.addApi(key, this.pooledAddedApi[key], this);
      }
      for(const key in this.pooledAddedCallerApi) {
        (this.process as any).addApi({ name: key as any, system: this.pooledAddedCallerApi[key] as any }, this);
      }
      // clear pools
      Object.keys(this.pooledAddedApi).forEach(k => delete this.pooledAddedApi[k]);
      Object.keys(this.pooledAddedCallerApi).forEach(k => delete this.pooledAddedCallerApi[k]);
    }

    public clearEventListeners() {
        Object.keys(this.entityEventHandlers).forEach(entityId => {
            const { entity, listeners } = this.entityEventHandlers[entityId];
            Object.keys(listeners).forEach(k => this.offEntityEvent(entity, k));
        });
    }

    public handleEntityRemovedComponent(entity: Entity, component: TComponent, options?: any) {
        if(this.entityEventHandlers[entity.id]) {
            const eventNameKeys = Object.keys(this.entityEventHandlers[entity.id].listeners);
            eventNameKeys.forEach(k => this.offEntityEvent(entity, k));
        }
        this.__untrackEntity(entity);
        this.onEntityRemovedComponent?.(entity, component, options);
    }

  // helper function for systems that will store callback references per entity to track and remove when entity removes component.
  public onEntityEvent(entity: Entity, eventName: string, callback: (eventData: any) => void) {
    if(!(entity.id in this.entityEventHandlers)) {
      this.entityEventHandlers[entity.id] = { entity, listeners: {} };
    }
    if(this.entityEventHandlers[entity.id].entity !== entity) {
      throw new Error(`Trying to listen on entity event with same id ${entity.id} but different entity was already registered.. this means you probably have two entities with the same id which is BAD.`)
    }
    if(this.entityEventHandlers[entity.id].listeners[eventName]) {
      throw new Error(`Already have a registered event for event: ${eventName} on entity: ${entity.id} for system: ${this.name}. Systems can only have 1 callback per entity event.`)
    }
    this.entityEventHandlers[entity.id].listeners[eventName] = callback;
    entity.on(eventName, callback);
  }

  public offEntityEvent(entity, eventName: string) : boolean {
    if(!(entity.id in this.entityEventHandlers)) return false;
    if(this.entityEventHandlers[entity.id].entity !== entity) {
      throw new Error(`Trying to listen on entity event with same id ${entity.id} but different entity was already registered.. this means you probably have two entities with the same id which is BAD.`)
    }
    const callback = this.entityEventHandlers[entity.id].listeners[eventName];
    if(!callback) return false;
    entity.off(eventName, callback);
    delete this.entityEventHandlers[entity.id].listeners[eventName];
    if(!(Object.keys(this.entityEventHandlers[entity.id].listeners).length)) {
        delete this.entityEventHandlers[entity.id];
    }
    return true;
  }

  public dispatchToSystem: (message: Message) => void;
  public dispatchToAllSystems:  (message: Omit<LocalSystemMessage, 'to'>) => void;
  public instantDispatchToSystem: (message: LocalSystemMessage) => void;
  public instantDispatchToAllSystems: (message: Omit<LocalSystemMessage, 'to'>) => void;
  public onBeforeRestart?(): any;
  public onAfterRestartBeforeInit?(returnObjectFromBeforeRestart: any): any;
  public onAfterRestart?(returnObjectFromBeforeRestart: any): any;
  public onMasterGlobalBroadcast?(message: ReceivedMessage) : void
  public onMasterSystemMessage?(message: ReceivedMessage) : void
  public onLocalSystemMessage?(message: ReceivedLocalSystemMessage) : void
  public onEntityAddedComponent?(entity: Entity, component: TComponent)
  public onEntityRemovedComponent?(entity: Entity, component: TComponent, options?: any)
  /** Fired when an asset visible to this process is created or overwritten. */
  public onAssetUpdated?(change: AssetChange): void;
  /** Fired when an asset visible to this process is deleted. */
  public onAssetRemoved?(change: AssetChange): void;
  public onUpdate?(delta: number) : void;
  public onStart?() : void;
  public onInit?() : void;
  public onStop?(...opts: any[]) : void;
  public onClear?(...opts: any[]) : void;

  // Schedule callbacks for the next tick phases via the owning Process
  public nextTickBeforeUpdate(cb: (delta: number) => void) {
    if(!this.process) {
      throw new Error(`System: ${this.name} tried to schedule nextTickBeforeUpdate before being attached to a process.`);
    }
    this.process.nextTickBeforeUpdate(cb);
  }
  public nextTickAfterUpdate(cb: (delta: number) => void) {
    if(!this.process) {
      throw new Error(`System: ${this.name} tried to schedule nextTickAfterUpdate before being attached to a process.`);
    }
    this.process.nextTickAfterUpdate(cb);
  }
}